/**
 * Cart Redirect and Mini Cart Auto-Open Functionality
 *
 * This script handles two main functions:
 * 1. Redirects users from /cart to the current page with ?with-minicart-open=true
 *    (skipped in Therabody mobile app WebView — opens cart drawer on /cart instead)
 * 2. Automatically opens the mini cart when the with-minicart-open parameter is present
 */

;(function () {
  "use strict"

  // Flag to prevent multiple redirects
  let isRedirecting = false
  let redirectTimeout = null
  let cartPageDetected = false

  // Detect mobile device for additional protection
  const isMobile =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent,
    )

  /** Same detection as snippets/mobile-app-webview.liquid (TherabodyApp UA + html class). */
  function isTherabodyMobileAppWebview() {
    try {
      if (document.documentElement.classList.contains("mobile-app-webview")) {
        return true
      }
    } catch (_) {}
    return (
      (navigator.userAgent || "").toLowerCase().indexOf("therabodyapp") !== -1
    )
  }

  // Function to create and show redirect overlay
  function createRedirectOverlay() {
    // Remove any existing overlay first
    const existingOverlay = document.getElementById("cart-redirect-overlay")
    if (existingOverlay) {
      existingOverlay.remove()
    }

    // Create overlay element
    const overlay = document.createElement("div")
    overlay.id = "cart-redirect-overlay"
    overlay.style.cssText = `
      position: fixed;
      top: 120px;
      left: 0;
      width: 100%;
      height: calc(100vh - 120px);
      z-index: 9999;
      display: flex;
      align-items: center;
      justify-content: center;
    `

    // Create message container
    const messageContainer = document.createElement("div")
    messageContainer.style.cssText = `
      padding: 40px;
      border-radius: 8px;
      text-align: center;
      max-width: 400px;
      margin: 20px;
    `

    // Create spinner
    const spinner = document.createElement("div")
    spinner.style.cssText = `
      width: 40px;
      height: 40px;
      border: 4px solid #f3f3f3;
      border-top: 4px solid #007cba;
      border-radius: 50%;
      animation: spin 1s linear infinite;
      margin: 0 auto 20px;
    `

    // Create GIF image
    const gifImage = document.createElement("img")
    // Use direct path to the GIF in assets folder
    gifImage.src =
      "https://cdn.shopify.com/s/files/1/0872/9257/6044/files/icons8-spinner.gif?v=1755797311"
    gifImage.style.cssText = `
      width: 80px;
      height: 80px;
      margin: 0 auto 20px;
      display: block;
    `
    gifImage.alt = "Redirecting..."

    // Add CSS animation for spinner
    const style = document.createElement("style")
    style.textContent = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `

    // Assemble overlay
    messageContainer.appendChild(style)
    messageContainer.appendChild(spinner)
    messageContainer.appendChild(gifImage)
    overlay.appendChild(messageContainer)

    // Add to body
    document.body.appendChild(overlay)

    // Prevent any clicks on the overlay
    overlay.addEventListener("click", function (e) {
      e.preventDefault()
      e.stopPropagation()
    })

    // Prevent any form submissions or interactions
    overlay.addEventListener("submit", function (e) {
      e.preventDefault()
      e.stopPropagation()
    })

    return overlay
  }

  // Function to check if we're on the cart page
  function isOnCartPage() {
    const pathname = window.location.pathname
    // Check for cart page in any locale (e.g., /cart, /es-eu/cart, /fr-fr/cart, etc.)
    const isCart =
      pathname === "/cart" ||
      pathname.endsWith("/cart") ||
      pathname.includes("/cart")

    return isCart
  }

  // Function to get the current page URL without the cart path
  function getCurrentPageUrl() {
    // If we're on the cart page, try to get the referrer or default to home
    if (isOnCartPage()) {
      // Check if there's a referrer and it's from the same domain
      if (
        document.referrer &&
        document.referrer.includes(window.location.hostname)
      ) {
        const referrerUrl = new URL(document.referrer)
        // Remove any existing with-minicart-open parameter
        referrerUrl.searchParams.delete("with-minicart-open")
        return referrerUrl.toString()
      }

      // If no valid referrer, preserve the locale path
      const currentPath = window.location.pathname

      // Try multiple locale patterns
      const localePatterns = [
        /^\/([a-z]{2}-[a-z]{2})\//, // fr-eu, es-eu, etc.
        /^\/([a-z]{2})\//, // fr, es, de, etc.
        /^\/([a-z]{2}_[a-z]{2})\//, // fr_eu, es_eu, etc.
      ]

      let localeMatch = null
      let matchedPattern = null

      for (const pattern of localePatterns) {
        localeMatch = currentPath.match(pattern)
        if (localeMatch) {
          matchedPattern = pattern.source
          break
        }
      }

      if (localeMatch) {
        const localePath = `/${localeMatch[1]}/`
        const localeUrl = window.location.origin + localePath
        return localeUrl
      }

      // Default to home page if no locale detected
      return window.location.origin
    }

    // If we're not on the cart page, return current URL
    const currentUrl = new URL(window.location.href)
    // Remove any existing with-minicart-open parameter
    currentUrl.searchParams.delete("with-minicart-open")
    return currentUrl.toString()
  }

  // Function to redirect to current page with mini cart parameter
  function redirectToCurrentPageWithMiniCart() {
    // Prevent multiple redirects
    if (isRedirecting) {
      return
    }

    // Prevent redirect if already in progress (but allow initial cart page redirect)
    if (isRedirecting) {
      return
    }

    // Set redirecting flag
    isRedirecting = true

    // Add a small delay to ensure cart page is fully loaded before showing overlay
    // Use longer delay on mobile to prevent rapid tapping issues
    const initialDelay = isMobile ? 500 : 300

    redirectTimeout = setTimeout(() => {
      // Show overlay to prevent user interaction
      const overlay = createRedirectOverlay()

      const targetUrl = getCurrentPageUrl()
      const separator = targetUrl.includes("?") ? "&" : "?"
      const redirectUrl = `${targetUrl}${separator}with-minicart-open=true`

      // Add another delay to ensure overlay is visible before redirect
      // Use longer delay on mobile
      const redirectDelay = isMobile ? 1500 : 1000

      setTimeout(() => {
        // Use replace to avoid adding to browser history
        window.location.replace(redirectUrl)
      }, redirectDelay)
    }, initialDelay)

    // Fallback redirect in case the main redirect fails
    // Use longer timeout on mobile
    const fallbackTimeout = isMobile ? 8000 : 5000
    setTimeout(() => {
      if (isOnCartPage() && isRedirecting) {
        const targetUrl = getCurrentPageUrl()
        const separator = targetUrl.includes("?") ? "&" : "?"
        const redirectUrl = `${targetUrl}${separator}with-minicart-open=true`
        window.location.replace(redirectUrl)
      }
    }, fallbackTimeout)
  }

  // Function to check if mini cart should auto-open
  function shouldAutoOpenMiniCart() {
    const urlParams = new URLSearchParams(window.location.search)
    return urlParams.get("with-minicart-open") === "true"
  }

  // Function to open the mini cart
  function openMiniCart() {
    // Wait for the cart drawer to be available
    const waitForCartDrawer = setInterval(() => {
      const cartDrawer = document.querySelector("cart-drawer")
      if (cartDrawer && typeof cartDrawer.open === "function") {
        clearInterval(waitForCartDrawer)

        // Small delay to ensure everything is loaded
        setTimeout(() => {
          cartDrawer.open()

          // Clean up the URL parameter without adding to browser history
          const cleanUrl = new URL(window.location.href)
          cleanUrl.searchParams.delete("with-minicart-open")
          window.history.replaceState({}, "", cleanUrl.toString())
        }, 100)
      }
    }, 100)

    // Timeout after 5 seconds to prevent infinite waiting
    setTimeout(() => {
      clearInterval(waitForCartDrawer)
    }, 5000)
  }

  /** On /cart in Therabody app WebView: no redirect; open theme cart drawer immediately. */
  function openMiniCartInstantForMobileApp() {
    function tryOpen() {
      const cartDrawer = document.querySelector("cart-drawer")
      if (cartDrawer && typeof cartDrawer.open === "function") {
        cartDrawer.open(null)
        return true
      }
      return false
    }
    if (tryOpen()) return
    customElements.whenDefined("cart-drawer").then(() => {
      if (tryOpen()) return
      requestAnimationFrame(() => {
        const t0 = Date.now()
        const id = window.setInterval(() => {
          if (tryOpen() || Date.now() - t0 > 8000) window.clearInterval(id)
        }, 50)
      })
    })
  }

  // Main execution logic
  function init() {
    // Check if we should redirect from cart page
    if (isOnCartPage()) {
      // Therabody app WebView: cart drawer open is handled by mobile-app-webview.js
      // (initMobileAppPendingScreen → cart scenario).
      if (isTherabodyMobileAppWebview()) {
        return
      }
      // Set protection flag to prevent further interactions
      cartPageDetected = true

      // Apply cart navigation protection immediately
      preventCartNavigation()

      // Show overlay and redirect immediately
      redirectToCurrentPageWithMiniCart()
      return
    }

    // Check if we should auto-open mini cart
    if (shouldAutoOpenMiniCart()) {
      // Wait for DOM to be ready
      if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", openMiniCart)
      } else {
        openMiniCart()
      }
    }
  }

  // Additional protection: Check if we're on cart page after DOM loads
  // This handles cases where the script runs before the URL is fully processed
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      if (isOnCartPage() && !isTherabodyMobileAppWebview()) {
        redirectToCurrentPageWithMiniCart()
      }
    })
  }

  // Prevent rapid cart icon clicks from causing infinite loops
  function preventRapidCartClicks() {
    const cartIcon = document.querySelector("#cart-icon-bubble")
    if (cartIcon && !cartIcon.dataset.clickProtected) {
      cartIcon.dataset.clickProtected = "true"

      // Add comprehensive protection for both click and touch events
      const preventCartInteraction = function (e) {
        if (isRedirecting || cartPageDetected) {
          e.preventDefault()
          e.stopPropagation()
          e.stopImmediatePropagation()
          return false
        }

        // Prevent cart icon click when already on cart page (not in Therabody app WebView)
        if (isOnCartPage() && !isTherabodyMobileAppWebview()) {
          e.preventDefault()
          e.stopPropagation()
          e.stopImmediatePropagation()
          return false
        }
      }

      // Prevent clicks
      cartIcon.addEventListener("click", preventCartInteraction, true)

      // Prevent touch events (mobile)
      cartIcon.addEventListener("touchstart", preventCartInteraction, true)
      cartIcon.addEventListener("touchend", preventCartInteraction, true)
      cartIcon.addEventListener("touchmove", preventCartInteraction, true)

      // Prevent pointer events
      cartIcon.addEventListener("pointerdown", preventCartInteraction, true)
      cartIcon.addEventListener("pointerup", preventCartInteraction, true)

      // Prevent mousedown/mouseup (desktop)
      cartIcon.addEventListener("mousedown", preventCartInteraction, true)
      cartIcon.addEventListener("mouseup", preventCartInteraction, true)

      // Disable the cart icon completely during redirect or cart page
      cartIcon.addEventListener(
        "click",
        function (e) {
          if (isRedirecting || cartPageDetected) {
            e.preventDefault()
            e.stopPropagation()
            cartIcon.style.pointerEvents = "none"
            cartIcon.style.opacity = "0.5"

            return false
          }
        },
        true,
      )
    }
  }

  // Call protection function when DOM is ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", preventRapidCartClicks)
  } else {
    preventRapidCartClicks()
  }

  // Initialize when script loads
  init()

  // Immediate cart icon protection if we're already on a cart page (Web storefront only)
  if (isOnCartPage() && !isTherabodyMobileAppWebview()) {
    // Set protection flag to prevent further interactions
    cartPageDetected = true

    const cartIcon = document.querySelector("#cart-icon-bubble")
    if (cartIcon) {
      cartIcon.style.pointerEvents = "none"
      cartIcon.style.opacity = "0.5"
    }

    // Override cart drawer open functionality when on cart page
    const cartDrawer = document.querySelector("cart-drawer")
    if (cartDrawer && typeof cartDrawer.open === "function") {
      const originalOpen = cartDrawer.open
      cartDrawer.open = function (triggeredBy) {
        if (isOnCartPage()) {
          return false
        }
        return originalOpen.call(this, triggeredBy)
      }
    }

    // Apply comprehensive cart navigation protection
    preventCartNavigation()
  }

  // Global protection during redirect (catches any missed interactions)
  document.addEventListener(
    "click",
    function (e) {
      if (
        (isRedirecting || cartPageDetected) &&
        e.target.closest("#cart-icon-bubble")
      ) {
        e.preventDefault()
        e.stopPropagation()

        return false
      }
    },
    true,
  )

  // Additional mobile protection
  if (isMobile) {
    document.addEventListener(
      "touchstart",
      function (e) {
        if (
          (isRedirecting || cartPageDetected) &&
          e.target.closest("#cart-icon-bubble")
        ) {
          e.preventDefault()
          e.stopPropagation()

          return false
        }
      },
      true,
    )

    // Additional mobile touch events
    document.addEventListener(
      "touchend",
      function (e) {
        if (
          (isRedirecting || cartPageDetected) &&
          e.target.closest("#cart-icon-bubble")
        ) {
          e.preventDefault()
          e.stopPropagation()

          return false
        }
      },
      true,
    )
  }

  // Also handle navigation events for SPA-like behavior
  window.addEventListener("popstate", function () {
    if (shouldAutoOpenMiniCart()) {
      openMiniCart()
    }
  })

  // Cleanup on page unload to prevent memory leaks
  window.addEventListener("beforeunload", function () {
    resetRedirectState()
  })

  // Function to reset redirect state
  function resetRedirectState() {
    isRedirecting = false
    cartPageDetected = false
    if (redirectTimeout) {
      clearTimeout(redirectTimeout)
      redirectTimeout = null
    }

    // Re-enable cart icon
    const cartIcon = document.querySelector("#cart-icon-bubble")
    if (cartIcon) {
      cartIcon.style.pointerEvents = "auto"
      cartIcon.style.opacity = "1"
    }
  }

  // Function to prevent cart navigation when on cart page
  function preventCartNavigation() {
    // Override any cart-related navigation
    const cartLinks = document.querySelectorAll(
      'a[href*="/cart"], a[href*="cart"]',
    )
    cartLinks.forEach((link) => {
      if (link.dataset.cartProtectionApplied) return

      link.dataset.cartProtectionApplied = "true"
      const originalHref = link.href

      link.addEventListener(
        "click",
        function (e) {
          if (isOnCartPage()) {
            e.preventDefault()
            e.stopPropagation()

            return false
          }
        },
        true,
      )
    })

    // Override cart icon click events
    const cartIcon = document.querySelector("#cart-icon-bubble")
    if (cartIcon && !cartIcon.dataset.cartNavigationProtected) {
      cartIcon.dataset.cartNavigationProtected = "true"

      // Remove any existing click handlers that might open cart drawer
      const clonedIcon = cartIcon.cloneNode(true)
      cartIcon.parentNode.replaceChild(clonedIcon, cartIcon)

      // Re-apply our protection
      preventRapidCartClicks()
    }
  }

  // Export functions for potential external use
  window.CartRedirect = {
    openMiniCart: openMiniCart,
    openMiniCartInstantForMobileApp: openMiniCartInstantForMobileApp,
    shouldAutoOpenMiniCart: shouldAutoOpenMiniCart,
    resetRedirectState: resetRedirectState,
  }
})()
